/* Mirrors andavo-dev's Nomad Button (libs/client/web/shared/ui/nomad/src/lib/Button).
   Not imported from there — andavo-dev is a separate repo; we reproduce the cva
   variants 1:1 against the vendored design tokens so the look is native. */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../util/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-inter font-semibold ring-offset-surface-background transition-colors focus-visible:border-2 focus-visible:border-border-action focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // primary = dark-slate fill (neutral-800), inverted text
        primary:
          'bg-button-primary text-content-primary-inverted hover:bg-button-primary-hover active:bg-button-primary-pressed',
        // secondary = filled neutral-100
        secondary:
          'bg-button-secondary text-content-primary hover:bg-button-secondary-hover active:bg-button-secondary-pressed [&>svg]:text-content-secondary',
        // danger = transparent + danger border
        danger:
          'border border-border-button-danger text-content-danger hover:bg-button-danger-hover active:bg-button-danger-pressed',
        ghost: 'hover:bg-surface-foreground hover:text-content-primary',
        // link = brand (cyan) text
        link: 'text-content-action decoration-solid underline-offset-4 hover:underline',
        outline:
          'border border-border-subtle bg-surface-background hover:bg-surface-foreground hover:text-content-primary',
      },
      size: {
        default: 'h-10 px-4 py-2 text-sm',
        link: 'p-1',
        xs: 'p-3 text-xs',
        sm: 'h-9 px-3 text-sm',
        lg: 'h-11 px-8 text-base',
        xl: 'h-13 px-5 text-base',
        icon: 'h-8 w-8',
      },
      borderRadius: {
        square: 'rounded-md',
        round: 'rounded-full',
        sharp: 'rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      borderRadius: 'square',
    },
  },
);

export type ButtonProps = {
  asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, borderRadius, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, borderRadius, className }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
